import {PropTypes} from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {
  createDocument,
  createDocumentVersion,
  updateDocument
} from '../script/document-service';
import {addNotifications} from '../store/appActions';
import {UPLOAD_ACTION_TYPE} from '../utils/constants';
import DocumentBrowser from './DocumentBrowser';
import DocumentDetails from './DocumentDetails';
import DocumentUpload from './DocumentUpload';

const VIEW_MODE_BROWSER = 'VIEW_BROWSER';
const VIEW_MODE_UPLOAD = 'VIEW_UPLOAD';
const VIEW_MODE_VERSION = 'VIEW_VERSION';
const VIEW_MODE_DETAILS = 'VIEW_DETAILS';

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    addNotifications: (notification) => dispatch(addNotifications(notification))
  };
}

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state) => {
  return {
    archives: state.archives,
    user: state.user
  };
};

/**
 * Connected component class of DocumentBrowserApp.
 *
 */
class ConnectedDocumentBrowserApp extends Component {
  /**
   * Creates a DocumentBrowserApp.
   *
   * @param {Object} props - The property data.
   */
  constructor(props) {
    super(props);
    this.browserRef = React.createRef();
    const mainArchiveAccess = Object.values(this.props.archives)[0];
    this.state = {
      archives: this.props.archives,
      archiveAccess: 'READ',
      documentId: null,
      viewMode: VIEW_MODE_BROWSER,
      selectedFileId: null,
      uploadActionType: null,
      archiveId: mainArchiveAccess.archiveId,
      mainArchiveId: mainArchiveAccess.archiveId
    };
  }

  /**
   * Changes the components displayed.
   *
   * @param {Object} document - The object containing document details.
   * @param {string} archiveAccess - READ or WRITE.
   */
  documentDetailsToggle = (document, archiveAccess) => {
    this.setState({
      viewMode: VIEW_MODE_DETAILS,
      documentId: document ? document.fileId : null,
      archiveAccess: archiveAccess
    });
  };

  /**
   * Changes the components displayed by opening the upload form.
   *
   */
  documentUploadToggle = () => {
    this.setState({
      documentId: null,
      selectedFileId: null,
      viewMode: VIEW_MODE_UPLOAD,
      uploadActionType: UPLOAD_ACTION_TYPE.NEW_DOCUMENT
    });
  };

  /**
   * Setting the state for upload of new document's version.
   *
   * @param {string} documentId - document's id
   */
  documentNewVersionUploadToggle = (documentId) => {
    this.setState({
      viewMode: VIEW_MODE_VERSION,
      selectedFileId: documentId,
      uploadActionType: UPLOAD_ACTION_TYPE.NEW_VERSION
    });
  };

  /**
   * Handles the submit of the add document button.
   *
   * @param {Object} metadata - Metadata from submit action.
   */
  handleDocumentSubmit = (metadata) => {
    if (this.state.documentId != null) {
      updateDocument(
        metadata,
        this.state.documentId,
        this.state.archiveId
      ).then((response) => {
        if (response.id != null) {
          this.props.addNotifications([
            new Notification(
              locale.updateDocumentCompleteNotification,
              'success',
              6000
            )
          ]);
          if (response.testament === 'deleted') {
            window.location.reload();
          } else {
            this.setState({viewMode: VIEW_MODE_BROWSER});
          }
        } else if (response.error) {
          this.props.addNotifications([
            new Notification(locale.getString(response.error), 'error', 6000)
          ]);
        } else {
          this.props.addNotifications([
            new Notification(
              locale.updateDocumentErrorNotification,
              'error',
              6000
            )
          ]);
        }
      });
    }
  };

  /**
   * Handles the submit of the file upload button.
   *
   * @param {File} file - The file to be uploaded.
   * @param {sting} actionType - type of the upload action.
   */
  handleFileSubmit = async (file, actionType) => {
    return new Promise((resolve) => {
      if (actionType == UPLOAD_ACTION_TYPE.NEW_DOCUMENT) {
        createDocument(file, this.state.archiveId).then((response) => {
          this.handleSubmitResponse(response, actionType);
          resolve(response);
        });
      } else {
        createDocumentVersion(
          file,
          this.state.selectedFileId,
          this.state.archiveId
        ).then((response) => {
          this.handleSubmitResponse(response, actionType);
          resolve(response);
          if (!response.error) {
            this.showDocumentBrowser();
          }
        });
      }
    });
  };

  /**
   * Get the error text for upload response.
   *
   * @param {string} actionType - Type of the upload action.
   * @return {string} - The response text,
   */
  getErrorText = (actionType) => {
    const notificationText =
      actionType == UPLOAD_ACTION_TYPE.NEW_DOCUMENT
        ? locale.uploadingDocumenErrorNotification
        : locale.uploadingNewVersionErrorNotification;
    return notificationText;
  };

  /**
   * Handles the submit feedback.
   *
   * @param {Object} response - The json response from service.
   * @param {string} actionType - type of the upload action.
   */
  handleSubmitResponse = (response, actionType) => {
    if (response.id != null) {
      this.setState({documentId: response.id});
      this.props.addNotifications([
        new Notification(
          actionType == UPLOAD_ACTION_TYPE.NEW_DOCUMENT
            ? locale.uploadingDocumentCompleteNotification
            : locale.uploadingNewVersionCompleteNotification,
          'success',
          6000
        )
      ]);
    } else {
      this.props.addNotifications([
        new Notification(this.getErrorText(actionType), 'error', 6000)
      ]);
    }
  };

  /**
   * Renders form for upload of new document's version.
   *
   * @return {string} - the upload form for new document's version.
   */
  renderNewVersionUploadForm = () => {
    return (
      <div id='document-new-version-upload'>
        <DocumentUpload
          archiveId={this.state.archiveId}
          archiveOwner={this.getArchiveOwnerName()}
          handleFileSubmit={this.handleFileSubmit}
          mainArchiveId={this.state.mainArchiveId}
          onBackLinkClick={this.showDocumentBrowser}
          raiseNotification={this.raiseNotification}
          uploadActionType={this.state.uploadActionType}
          user={this.props.user}
        />
      </div>
    );
  };

  /**
   * Sets view mode to the document browser.
   */
  showDocumentBrowser = () => {
    this.setState({viewMode: VIEW_MODE_BROWSER});
  };

  getArchiveOwnerName = () => {
    return this.state.archives.find(
      (ac) => ac.archiveId == this.state.archiveId
    ).archiveOwnerName;
  };

  raiseNotification = (message, type = 'info') => {
    this.props.addNotifications([new Notification(message, type, 6000)]);
  };

  /**
   * Renders the DocumentBrowserApp component.
   *
   * @return {string} - The document browser application component to be rendered.
   */
  render() {
    return (
      <div id='document-browser-app'>
        <div
          id='document-browser'
          hidden={this.state.viewMode !== VIEW_MODE_BROWSER}
        >
          <DocumentBrowser
            ref={this.browserRef}
            archives={this.props.archives}
            onSelectArchive={(archiveId) =>
              this.setState({archiveId: archiveId})
            }
            onDocumentDetailsShown={this.documentDetailsToggle}
            onDocumentUploadShown={this.documentUploadToggle}
            onDocumentNewVersionUploadShown={
              this.documentNewVersionUploadToggle
            }
          />
        </div>
        {this.state.viewMode === VIEW_MODE_DETAILS && (
          <div id='document-details'>
            <DocumentDetails
              handleDocumentSubmit={this.handleDocumentSubmit}
              onBackLinkClick={this.showDocumentBrowser}
              archiveAccess={this.state.archiveAccess}
              documentId={this.state.documentId}
            />
          </div>
        )}
        {this.state.viewMode === VIEW_MODE_UPLOAD && (
          <div id='document-upload'>
            <DocumentUpload
              archiveId={this.state.archiveId}
              archiveOwner={this.getArchiveOwnerName()}
              documentId={this.state.documentId}
              handleDocumentSubmit={this.handleDocumentSubmit}
              handleFileSubmit={this.handleFileSubmit}
              mainArchiveId={this.state.mainArchiveId}
              onBackLinkClick={this.showDocumentBrowser}
              raiseNotification={this.raiseNotification}
              uploadActionType={this.state.uploadActionType}
              user={this.props.user}
            />
          </div>
        )}
        {this.state.viewMode === VIEW_MODE_VERSION &&
          this.renderNewVersionUploadForm()}
      </div>
    );
  }
}

ConnectedDocumentBrowserApp.propTypes = {
  addNotifications: PropTypes.func.isRequired,
  archives: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired
};

const DocumentBrowserApp = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConnectedDocumentBrowserApp);
export default DocumentBrowserApp;
